import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { URL } from "../../../store/actions/depannageActions"
import {
	handleErrors,
	normalizeApiError,
	showToastFromError,
} from "../../../store/actions/apiHelpers"
import { showToast } from "../../../store/actions/toastActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { useGroups } from "../../../hooks/groups"
import CustomModal from "../../basics/interface/CustomModal"
import Loader from "../../basics/Loader"
import { useQuitGuard } from "../../../hooks/useQuitGuard"
import { BARRE_FIXE_BAS } from "../../../utilities/versionDeploiement"

// Sous-onglet « Tarifs privés » (GE-66, amendement A1 du 13.10) — gabarit de
// TarifsPoliceV2 (carte, rail des versions, colonne collante, sections,
// écarts surlignés, barre d'enregistrement, modals). Maquette validée :
// docs/depannage-prive/maquette-tarifs-prives-config.html. Colonnes lieu ×
// poids × moment ; lignes 1.x / 2.x = GrillePriveCellule, 3 à 6 =
// GrillePriveSupplement (une valeur pour tous les lieux et poids). Les codes
// MIRRORENT svp/prive_tarifs.py. Lecture pour tous, écriture boss (serveur).

const ETAT_BROUILLON = 0
const ETAT_EN_VIGUEUR = 1
const ETAT_ARCHIVEE = 2

const LIEUX = [
	{ code: "ROU", label: "Route" },
	{ code: "SOU", label: "Sous-sol ***" },
	{ code: "AUT", label: "Autoroute" },
]
const POIDS = [
	{ code: "M2T", label: "< 2 t" },
	{ code: "P2T", label: "> 2 t" },
]
const MOMENTS = [
	{ code: "JOUR", label: "J" },
	{ code: "NUIT", label: "N" },
]
const COLS = []
LIEUX.forEach((l, li) =>
	POIDS.forEach((p, pi) =>
		MOMENTS.forEach((m, mi) =>
			COLS.push({
				lieu: l.code,
				poids: p.code,
				moment: m.code,
				zoneStart: li > 0 && pi === 0 && mi === 0,
				poidsStart: pi > 0 && mi === 0,
			}),
		),
	),
)

// Règle (svp/prive_tarifs.sur_place_possible) : sur place = panne et lieu ≠
// autoroute ; un accident n'a jamais de ligne sur place.
const BASE = [
	{
		titre: "1. Panne",
		rows: [
			{
				code: "1.1",
				label: "Réparé sur place",
				note: "pas sur autoroute *",
				situation: "PAN",
				issue: "SP",
			},
			{
				code: "1.2",
				label: "Transporté **",
				situation: "PAN",
				issue: "TR",
			},
		],
	},
	{
		titre: "2. Accident",
		rows: [
			{
				code: "2.1",
				label: "Transporté **",
				note: "transport d'office, pas de sur place *",
				situation: "ACC",
				issue: "TR",
			},
		],
	},
]
const dispo = (row, lieu) => row.issue === "TR" || lieu !== "AUT"

// Suppléments : codes de la grille ; « compris » / « devis » / « fixe » ne
// sont pas des montants saisis.
const SUPP = [
	{
		titre: "3. Km en charge (Suisse)",
		rows: [
			{
				code: "3.0",
				label: "Jusqu'à 20 km",
				note: "lieu → destination (la nuit : → dépôt) · transporté seulement",
				texte: "compris",
			},
			{ code: "3.1", label: "21 à 30 km", u: "KM_CH_T1" },
			{ code: "3.2", label: "31 à 40 km", u: "KM_CH_T2" },
			{ code: "3.3", label: "41 à 50 km", u: "KM_CH_T3" },
			{ code: "3.4", label: "Plus de 50 km", texte: "devis à part" },
		],
	},
	{
		titre: "4. France",
		rows: [
			{
				code: "4.1",
				label: "Zone 1, jusqu'à 50 km",
				note: "boucle base → base · remplace les km Suisse (pas de cumul)",
				u: "FR_Z1",
			},
			{ code: "4.2", label: "Zone 2, 51 à 99 km", u: "FR_Z2" },
			{ code: "4.3", label: "100 km et plus", texte: "devis à part" },
		],
	},
	{
		titre: "5. Temps et grutage",
		rows: [
			{
				code: "5.0",
				label: "Temps compris sur place",
				note: "chargement complet · règle du moteur",
				fixe: "30",
				unite: "min",
			},
			{
				code: "5.1",
				label: "Temps supplémentaire",
				note: "par tranche complète de ½ h (attente, douane…)",
				jn: ["TEMPS_JOUR", "TEMPS_NUIT"],
			},
			{
				code: "5.2",
				label: "Grutage, 1re heure d'office",
				note: "2 personnes",
				jn: ["GRUTAGE_1H_JOUR", "GRUTAGE_1H_NUIT"],
			},
			{
				code: "5.3",
				label: "Grutage, ½ h au-delà",
				note: "par ½ h entamée",
				jn: ["GRUTAGE_TRANCHE_JOUR", "GRUTAGE_TRANCHE_NUIT"],
			},
		],
	},
	{
		titre: "6. Relivraison le lendemain",
		rows: [
			{
				code: "6.1",
				label: "Forfait dépôt → destination",
				note: "après un transport de nuit · sans calcul de km",
				u: "LIV_FORFAIT",
			},
			{
				code: "6.2",
				label: "+ France zone 1",
				note: "s'ajoute au forfait",
				u: "LIV_FR_Z1",
			},
			{ code: "6.3", label: "+ France zone 2", u: "LIV_FR_Z2" },
			{
				code: "6.4",
				label: "France 100 km et plus",
				texte: "devis à part",
			},
		],
	},
	{
		titre: "7. Autres",
		rows: [
			{
				code: "7.1",
				label: "Course à vide",
				// GE-66 J2 révisé (Jordan 13.10 soir) : montant unique.
				note: "Appliquée quand le diagnostic est Course à vide · forfait seul, sans supplément",
				u: "COURSE_A_VIDE",
			},
		],
	},
]
const TEMPS = { JOUR: "TEMPS_JOUR", NUIT: "TEMPS_NUIT" }

// Clés du brouillon : C|lieu|situation|moment|poids|issue (cellule) et
// S|code (supplément).
const cleCellule = (lieu, situation, moment, poids, issue) =>
	`C|${lieu}|${situation}|${moment}|${poids}|${issue}`
const cleSupp = (code) => `S|${code}`

const valeursDe = (grille) => {
	const out = {}
	;(grille?.cellules || []).forEach((c) => {
		out[cleCellule(c.lieu, c.situation, c.moment, c.poids, c.issue)] =
			c.prix
	})
	;(grille?.supplements || []).forEach((x) => {
		out[cleSupp(x.code)] = x.montant
	})
	return out
}

const dateFr = (iso) => (iso ? iso.split("-").reverse().join(".") : "")
// Revue A1 (🟠11) : la saisie reste en texte brut, convertie au blur ou à
// l'envoi. null = case vide (non disponible), undefined = invalide (négatif,
// non numérique) — jamais une valeur faussée en silence. Espaces et
// apostrophes de milliers tolérés, virgule décimale acceptée.
const lire = (brut) => {
	const v = String(brut)
		.trim()
		.replace(/[\s'’]/g, "")
		.replace(",", ".")
	if (v === "") return null
	if (!/^\d+(\.\d+)?$/.test(v)) return undefined
	return Number(v)
}

// Saisie d'une case : un prix de base (C|…) est > 0 (vide = non
// disponible) ; un supplément ≥ 0. undefined = invalide.
const lireCase = (k, brut) => {
	const n = lire(brut)
	if (n === 0 && k.startsWith("C|")) return undefined
	return n
}
// Supplément à montant (ni devis ni « compris ») resté vide : refusé par le
// serveur — signalé avant l'envoi.
const suppVide = (k, v) =>
	k.startsWith("S|") &&
	!k.endsWith("DEVIS") &&
	(v === null || v === undefined)

// Bordures du CSS global legacy (App.css : th / td nus) neutralisées.
const BORDURE = COLORS.border
const BORDURE_FORTE = COLORS.borderStrong
const sansBordure = {
	borderTop: "none",
	borderLeft: "none",
	borderRight: "none",
	borderBottom: `1px solid ${BORDURE}`,
}
// Fonds de la maquette.
const FOND_TETE = "#FAF8F5"
const FOND_TETE_N = "#F3F0EB"
const FOND_SECTION = "#F0EDE7"
const FOND_N = "#F7F4EF"
const FOND_N_ZEBRE = "#F4F1EB"
const FOND_ZEBRE = "#FCFBF9"
const FOND_SURVOL = "#F6F3EE"
const FOND_ECART = "#FFF7E6"

const bordureGauche = (c) =>
	c.zoneStart
		? `2px solid ${BORDURE_FORTE}`
		: c.poidsStart
			? `1px solid ${BORDURE}`
			: "none"

const styles = {
	card: {
		background: COLORS.surface,
		border: `1px solid ${BORDURE}`,
		borderRadius: 16,
		overflow: "hidden",
	},
	head: { padding: "20px 22px 16px" },
	title: {
		fontSize: 17,
		fontWeight: 600,
		letterSpacing: "-0.01em",
		color: COLORS.text,
	},
	sub: { fontSize: 13, color: COLORS.textMuted, marginTop: 3, maxWidth: 640 },
	rail: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		flexWrap: "wrap",
		padding: "0 22px 16px",
		borderBottom: `1px solid ${BORDURE}`,
	},
	railLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		marginRight: 4,
	},
	pill: (selected, brouillon) => ({
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		cursor: "pointer",
		border: `1px ${brouillon ? "dashed" : "solid"} ${
			selected ? COLORS.accent : BORDURE
		}`,
		borderRadius: 99,
		background: selected ? COLORS.accentLight : COLORS.surface,
		color: selected ? COLORS.accent : COLORS.textMuted,
		fontWeight: selected ? 600 : 500,
		padding: "6px 14px",
		fontFamily: "inherit",
	}),
	dot: (color) => ({
		width: 8,
		height: 8,
		borderRadius: 99,
		background: color,
	}),
	toolbar: {
		display: "flex",
		alignItems: "center",
		gap: 18,
		padding: "14px 22px",
		fontSize: 12.5,
		color: COLORS.textMuted,
	},
	legende: {
		flex: "1 1 auto",
		minWidth: 0,
		display: "flex",
		flexWrap: "wrap",
		columnGap: 14,
		rowGap: 2,
	},
	segment: { whiteSpace: "nowrap" },
	aideSaisie: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		cursor: "pointer",
		whiteSpace: "nowrap",
		flexShrink: 0,
	},
	lectureSeule: { whiteSpace: "nowrap", flexShrink: 0, fontSize: 13 },
	scroll: { overflowX: "auto", borderTop: `1px solid ${BORDURE}` },
	table: {
		borderCollapse: "separate",
		borderSpacing: 0,
		minWidth: 1080,
		width: "100%",
		fontVariantNumeric: "tabular-nums",
		userSelect: "text",
	},
	tbody: { userSelect: "text" },
	tr: (fond) => ({ backgroundColor: fond, cursor: "default" }),
	zoneHead: (zoneStart) => ({
		...sansBordure,
		borderBottom: `1px solid ${BORDURE_FORTE}`,
		borderLeft: zoneStart ? `2px solid ${BORDURE_FORTE}` : "none",
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: "0.09em",
		textTransform: "uppercase",
		color: COLORS.text,
		background: FOND_TETE,
		padding: "9px 6px",
		textAlign: "center",
	}),
	catHead: (zoneStart, poidsStart) => ({
		...sansBordure,
		borderLeft: zoneStart
			? `2px solid ${BORDURE_FORTE}`
			: poidsStart
				? `1px solid ${BORDURE}`
				: "none",
		fontWeight: 600,
		color: COLORS.text,
		fontSize: 12,
		padding: "8px 8px",
		background: FOND_TETE,
		textAlign: "center",
		lineHeight: 1.25,
	}),
	momentHead: (c) => ({
		...sansBordure,
		borderLeft: bordureGauche(c),
		width: 62,
		boxSizing: "border-box",
		color: COLORS.textLight,
		fontSize: 11.5,
		fontWeight: 600,
		letterSpacing: "0.06em",
		background: c.moment === "NUIT" ? FOND_TETE_N : FOND_TETE,
		padding: "7px 6px",
		textAlign: "center",
	}),
	stickyCol: (fond) => ({
		...sansBordure,
		position: "sticky",
		left: 0,
		zIndex: 2,
		background: fond,
		textAlign: "left",
		fontSize: 13,
		minWidth: 240,
		maxWidth: 300,
		borderRight: `1px solid ${BORDURE_FORTE}`,
		padding: "8px 14px",
	}),
	sectionRow: {
		...sansBordure,
		background: FOND_SECTION,
		color: COLORS.text,
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: "0.05em",
		textTransform: "uppercase",
		textAlign: "left",
		padding: "7px 14px",
	},
	code: {
		display: "inline-block",
		minWidth: 30,
		textAlign: "center",
		fontSize: 11.5,
		fontWeight: 700,
		color: COLORS.accent,
		background: COLORS.accentLight,
		borderRadius: 6,
		padding: "1px 5px",
		marginRight: 8,
	},
	prestaLabel: { fontSize: 13, fontWeight: 500, color: COLORS.text },
	prestaNote: {
		display: "block",
		fontSize: 11.5,
		color: COLORS.textLight,
		marginTop: 1,
		marginLeft: 38,
	},
	cell: (c, fond) => ({
		...sansBordure,
		borderLeft: bordureGauche(c),
		textAlign: "center",
		fontSize: 13,
		padding: "3px 3px",
		minWidth: 58,
		boxSizing: "border-box",
		background: fond,
	}),
	cellInput: ({ diff, invalide, focus, survol }) => ({
		font: "inherit",
		fontSize: 13,
		fontWeight: diff ? 600 : 400,
		letterSpacing: "normal",
		width: 54,
		boxSizing: "border-box",
		textAlign: "center",
		border: `1px solid ${
			invalide
				? COLORS.danger
				: focus
					? COLORS.accent
					: survol
						? BORDURE_FORTE
						: diff
							? "#F2D9A6"
							: "transparent"
		}`,
		borderRadius: 7,
		background:
			focus || survol
				? COLORS.surface
				: diff
					? FOND_ECART
					: "transparent",
		boxShadow: focus ? `0 0 0 2px ${COLORS.accentLight}` : "none",
		outline: "none",
		color: COLORS.text,
		padding: "5px 2px",
	}),
	cellValue: {
		display: "inline-block",
		fontSize: 13,
		fontWeight: 400,
		letterSpacing: "normal",
		color: COLORS.text,
		padding: "5px 2px",
	},
	na: { color: COLORS.textLight },
	suppCell: (fond) => ({
		...sansBordure,
		textAlign: "left",
		fontSize: 13,
		padding: "3px 14px",
		background: fond,
	}),
	suppVals: {
		display: "flex",
		alignItems: "center",
		gap: 18,
		flexWrap: "wrap",
	},
	suppMoment: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		fontSize: 12,
		color: COLORS.textLight,
		fontWeight: 600,
		letterSpacing: "0.06em",
	},
	suppBox: (diff) => ({
		display: "inline-block",
		borderRadius: 7,
		background: diff ? FOND_ECART : undefined,
	}),
	suppTexte: {
		fontSize: 13,
		color: COLORS.textMuted,
		padding: "5px 2px",
		fontStyle: "italic",
	},
	unite: { fontSize: 11, color: COLORS.textLight, marginLeft: 4 },
	tous: { fontSize: 11.5, color: COLORS.textLight, marginLeft: "auto" },
	footnotes: {
		padding: "14px 22px 18px",
		fontSize: 12,
		color: COLORS.textMuted,
		display: "grid",
		gap: 3,
	},
	// Barre fixe en bas d'écran (maquette) : position fixed, donc hors de
	// l'overflow hidden de la carte ; une cale réserve sa hauteur.
	savebar: {
		position: "fixed",
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 40,
		background: COLORS.surface,
		borderTop: `1px solid ${BORDURE}`,
		boxShadow: "0 -4px 16px rgba(28,25,23,0.08)",
	},
	// Aligné sur la carte (position et largeur mesurées).
	savebarInner: (cadre) => ({
		marginLeft: cadre ? cadre.left : 0,
		width: cadre ? cadre.width : "100%",
		boxSizing: "border-box",
		padding: "12px 22px",
		display: "flex",
		alignItems: "center",
		gap: 14,
	}),
	cale: { height: 72 },
	nowrap: { whiteSpace: "nowrap", flexShrink: 0 },
	btnDiscret: {
		...gs.btnSecondary,
		background: "transparent",
		border: "1px solid transparent",
		color: COLORS.textMuted,
	},
	dateInput: {
		font: "inherit",
		fontSize: 13,
		color: COLORS.text,
		border: `1px solid ${BORDURE_FORTE}`,
		borderRadius: 8,
		padding: "4px 8px",
		background: COLORS.surface,
	},
	erreurChargement: {
		padding: "14px 22px 18px",
		fontSize: 13,
		color: COLORS.danger,
	},
}

const Presta = ({ row, fond }) => (
	<td style={styles.stickyCol(fond)}>
		<span style={styles.code}>{row.code}</span>
		<span style={styles.prestaLabel}>{row.label}</span>
		{row.note && <span style={styles.prestaNote}>{row.note}</span>}
	</td>
)

const Section = ({ titre }) => (
	<tr className="no-hover" style={styles.tr(FOND_SECTION)}>
		<td
			style={{
				...styles.stickyCol(FOND_SECTION),
				...styles.sectionRow,
				borderRight: "1px solid transparent",
			}}
		>
			{titre}
		</td>
		<td style={styles.sectionRow} colSpan={COLS.length} />
	</tr>
)

const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? "s" : ""}`

const TarifsPrivesV2 = () => {
	const dispatch = useDispatch()
	const { token } = useSelector((s) => s.navInfo.login)
	const { isBoss } = useGroups()
	const [grilles, setGrilles] = useState(null)
	const [erreurChargement, setErreurChargement] = useState("")
	const [selectedId, setSelectedId] = useState(null)
	// Brouillon : valeurs converties (nombre | null) + saisies brutes en
	// cours ou invalides (texte), indépendantes de la version affichée.
	const [draft, setDraft] = useState({})
	const [brut, setBrut] = useState({})
	const [draftDate, setDraftDate] = useState("")
	const [autofillSous, setAutofillSous] = useState(true)
	// Modal : null | "sauver" | "vigueur" | "supprimer".
	const [confirm, setConfirm] = useState(null)
	const [etat, setEtat] = useState({ loading: false, erreur: "" })
	const [survolLigne, setSurvolLigne] = useState(null)
	const [survolCase, setSurvolCase] = useState(null)
	const [focusCase, setFocusCase] = useState(null)
	// Valeur d'une case au focus (aide « Sous-sol = Route + ½ h »).
	const avant = useRef({})
	// Carte mesurée : la barre fixe s'aligne dessus.
	const carte = useRef(null)
	const [cadre, setCadre] = useState(null)
	// Jeton du modal ouvert : une réponse arrivée après sa fermeture n'y
	// écrit plus d'erreur.
	const jeton = useRef(0)

	// (Re)charge la liste ; le brouillon local n'est remplacé QU'au
	// chargement et après une action serveur (revue A1, 🟠10).
	const appliquer = useCallback(
		(liste, id) => {
			setGrilles(liste)
			const brouillon = liste.find((g) => g.etat === ETAT_BROUILLON)
			setDraft(valeursDe(brouillon))
			setBrut({})
			setDraftDate(brouillon ? brouillon.valable_des : "")
			// Non-boss : la version en vigueur d'abord (le brouillon ne le
			// concerne pas).
			const enVigueur = liste.find((g) => g.etat === ETAT_EN_VIGUEUR)
			const cible =
				liste.find((g) => g.id === id) ||
				(isBoss ? brouillon || enVigueur : enVigueur || brouillon)
			setSelectedId(cible ? cible.id : null)
		},
		[isBoss],
	)

	const appel = useCallback(
		(method, corps) =>
			fetch(`${URL}api/prive/grilles`, {
				method,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: corps ? JSON.stringify(corps) : undefined,
			})
				.then(handleErrors)
				.then((r) => r.json()),
		[token],
	)

	useEffect(() => {
		appel("GET")
			.then((liste) => appliquer(liste, null))
			.catch((err) =>
				normalizeApiError(err).then((p) => {
					setErreurChargement(p.message)
					setGrilles([])
				}),
			)
	}, [appel, appliquer])

	const liste = grilles || []
	const selected = liste.find((g) => g.id === selectedId)
	const brouillon = liste.find((g) => g.etat === ETAT_BROUILLON)
	const enVigueur = liste.find((g) => g.etat === ETAT_EN_VIGUEUR)
	const editable = isBoss && selected?.etat === ETAT_BROUILLON
	const base = useMemo(() => valeursDe(enVigueur), [enVigueur])

	// Brouillon effectif : valeurs + saisies brutes encore valides.
	const effectif = useMemo(() => {
		const out = { ...draft }
		Object.entries(brut).forEach(([k, v]) => {
			const n = lireCase(k, v)
			if (n !== undefined) out[k] = n
		})
		return out
	}, [draft, brut])
	const invalides = useMemo(
		() =>
			Object.keys(brut)
				.filter((k) => lireCase(k, brut[k]) === undefined)
				.concat(
					Object.keys(effectif).filter(
						(k) => !(k in brut) && suppVide(k, effectif[k]),
					),
				),
		[brut, effectif],
	)
	const shown = editable ? effectif : valeursDe(selected)

	const diffKeys = useMemo(() => {
		if (!editable) return []
		const keys = new Set([...Object.keys(base), ...Object.keys(effectif)])
		return [...keys].filter(
			(k) => (effectif[k] ?? null) !== (base[k] ?? null),
		)
	}, [editable, effectif, base])

	// GE-66 J6 : saisie non enregistrée (écart au brouillon SERVEUR, date,
	// case invalide) → garde de sortie (navigation, onglet, deploy).
	const sale = useMemo(() => {
		if (!brouillon || !isBoss) return false
		const serveur = valeursDe(brouillon)
		const keys = new Set([
			...Object.keys(serveur),
			...Object.keys(effectif),
		])
		return (
			invalides.length > 0 ||
			draftDate !== brouillon.valable_des ||
			[...keys].some(
				(k) => (effectif[k] ?? null) !== (serveur[k] ?? null),
			)
		)
	}, [brouillon, isBoss, effectif, invalides, draftDate])
	const { setDirty, setClean, QuitGuard } = useQuitGuard()
	const avecBarre = !!(
		grilles &&
		isBoss &&
		brouillon &&
		selectedId === brouillon.id
	)
	useEffect(() => {
		if (!avecBarre) return
		const mesurer = () => {
			const r = carte.current && carte.current.getBoundingClientRect()
			if (r) setCadre({ left: r.left, width: r.width })
		}
		mesurer()
		window.addEventListener("resize", mesurer)
		// Revue passe 3 (🟡 D) : le bandeau « Nouvelle version » se pose
		// au-dessus de la barre (BandeauNouvelleVersion, --barre-fixe-bas).
		const racine = document.documentElement.style
		racine.setProperty(BARRE_FIXE_BAS, `${styles.cale.height}px`)
		return () => {
			window.removeEventListener("resize", mesurer)
			racine.removeProperty(BARRE_FIXE_BAS)
		}
	}, [avecBarre])
	useEffect(() => {
		if (sale) setDirty()
		else setClean()
	}, [sale, setDirty, setClean])

	if (grilles === null) return <Loader variant="ring" message="Chargement…" />

	// Aide de saisie (maquette) : le sous-sol suit la route + ½ h du moment
	// tant que sa case est vide ou encore égale à l'ancienne valeur dérivée.
	// Une route à 0 ne propage rien. Le sous-sol reste STOCKÉ case par case.
	const suivreRoute = (k, prev, now) => {
		if (!autofillSous || prev === now) return
		const p = k.split("|")
		setDraft((d) => {
			const next = { ...d }
			const suivre = (sousKey, ancien, nouveau) => {
				if (nouveau == null) return
				if (next[sousKey] == null || next[sousKey] === ancien)
					next[sousKey] = nouveau
			}
			if (p[0] === "C" && p[1] === "ROU") {
				if (!now) return next
				const temps = d[cleSupp(TEMPS[p[3]])] ?? 0
				suivre(
					cleCellule("SOU", p[2], p[3], p[4], p[5]),
					prev ? prev + temps : null,
					now + temps,
				)
			} else if (p[0] === "S" && Object.values(TEMPS).includes(p[1])) {
				const moment = p[1] === TEMPS.JOUR ? "JOUR" : "NUIT"
				Object.keys(d)
					.filter((x) => {
						const q = x.split("|")
						return q[0] === "C" && q[1] === "ROU" && q[3] === moment
					})
					.forEach((rk) => {
						const q = rk.split("|")
						const r = d[rk]
						if (r && prev != null && now != null)
							suivre(
								cleCellule("SOU", q[2], q[3], q[4], q[5]),
								r + prev,
								r + now,
							)
					})
			}
			return next
		})
	}

	// Blur : saisie valide → convertie (et l'aide suit) ; invalide → gardée
	// en texte, case en rouge, envoi bloqué.
	const valider = (k) => {
		setFocusCase(null)
		if (!(k in brut)) return
		const n = lireCase(k, brut[k])
		if (n === undefined) return
		setBrut((b) => {
			const next = { ...b }
			delete next[k]
			return next
		})
		setDraft((d) => ({ ...d, [k]: n }))
		suivreRoute(k, avant.current[k], n)
	}

	const saisie = (k, diff, ariaLabel) => {
		const invalide = invalides.includes(k) && focusCase !== k
		return (
			<input
				type="text"
				inputMode="decimal"
				aria-label={ariaLabel}
				aria-invalid={invalide}
				title={
					invalide
						? k.startsWith("C|")
							? "Prix positif attendu (vide = non disponible)"
							: "Montant requis, 0 ou plus"
						: undefined
				}
				style={styles.cellInput({
					diff,
					invalide,
					focus: focusCase === k,
					survol: survolCase === k,
				})}
				value={k in brut ? brut[k] : (draft[k] ?? "")}
				placeholder="—"
				onMouseEnter={() => setSurvolCase(k)}
				onMouseLeave={() => setSurvolCase(null)}
				onFocus={() => {
					setFocusCase(k)
					avant.current[k] = draft[k] ?? null
				}}
				onChange={(e) => {
					const v = e.target.value
					setBrut((b) => ({ ...b, [k]: v }))
				}}
				onBlur={() => valider(k)}
			/>
		)
	}
	const titreEcart = (k) =>
		`En vigueur : ${base[k] != null ? `${base[k]} CHF` : "non disponible"}`

	// Revue A1 (🟠10) : changer de pastille ne touche jamais au brouillon.
	const choisir = (g) => {
		if (g.id !== selectedId) setSelectedId(g.id)
	}
	const corpsSauver = () => ({
		action: "sauver",
		id: brouillon.id,
		valable_des: draftDate,
		cellules: Object.entries(effectif)
			.filter(([k, v]) => k.startsWith("C|") && v != null)
			.map(([k, prix]) => {
				const [, lieu, situation, moment, poids, issue] = k.split("|")
				return { lieu, situation, moment, poids, issue, prix }
			}),
		supplements: Object.entries(effectif)
			.filter(([k]) => k.startsWith("S|"))
			.map(([k, montant]) => {
				const code = k.slice(2)
				return {
					code,
					montant: code.endsWith("DEVIS") ? null : montant,
				}
			}),
	})
	const ouvrir = (quoi) => {
		jeton.current += 1
		setEtat({ loading: false, erreur: "" })
		setConfirm(quoi)
	}
	const fermer = () => {
		jeton.current += 1
		setConfirm(null)
		setEtat({ loading: false, erreur: "" })
	}
	const doAction = () => {
		if (confirm !== "supprimer" && invalides.length) {
			setEtat({
				loading: false,
				erreur: `${pluriel(invalides.length, "case")} en rouge : prix de base positif (vide = non disponible), supplément 0 ou plus.`,
			})
			return
		}
		const moi = jeton.current
		setEtat({ loading: true, erreur: "" })
		let suite
		let succes
		if (confirm === "sauver") {
			suite = appel("POST", corpsSauver())
			succes = "Brouillon enregistré"
		} else if (confirm === "vigueur") {
			// Enregistré PUIS mis en vigueur ; un refus du 1er arrête tout.
			// Passe 4 (🟡8) : la saisie enregistrée est adoptée dès le 1er
			// succès (garde de sortie désarmée même si la mise en vigueur
			// est refusée ensuite).
			suite = appel("POST", corpsSauver()).then((l) => {
				appliquer(l, brouillon.id)
				return appel("POST", {
					action: "mettre_en_vigueur",
					id: brouillon.id,
				})
			})
			succes = "Grille mise en vigueur"
		} else {
			suite = appel("DELETE", { id: brouillon.id })
			succes = "Brouillon supprimé"
		}
		const garder = confirm === "supprimer" ? null : brouillon.id
		return suite.then(
			(l) => {
				appliquer(l, garder)
				dispatch(showToast("success", succes))
				if (moi === jeton.current) fermer()
			},
			(err) =>
				normalizeApiError(err).then((p) => {
					if (moi === jeton.current)
						setEtat({ loading: false, erreur: p.message })
				}),
		)
	}
	const creerBrouillon = () =>
		appel("POST", { action: "creer_brouillon" }).then(
			(l) => {
				appliquer(l, null)
				dispatch(
					showToast(
						"success",
						"Brouillon créé depuis la version en vigueur",
					),
				)
			},
			(err) =>
				normalizeApiError(err).then((p) =>
					dispatch(showToastFromError(p)),
				),
		)

	const confirmTitles = {
		sauver: "Enregistrer le brouillon ?",
		vigueur: "Mettre la grille en vigueur ?",
		supprimer: "Supprimer le brouillon ?",
	}
	const nbEcarts = (
		<b style={{ color: COLORS.text }}>
			{pluriel(diffKeys.length, "cellule")}
		</b>
	)

	// Zébrage (maquette : nth-child pair, lignes de section comprises) et
	// survol, sur la ligne entière y compris la colonne collante.
	let rang = 0
	const fondLigne = (code) => {
		rang += 1
		if (survolLigne === code) return FOND_SURVOL
		return rang % 2 === 0 ? FOND_ZEBRE : COLORS.surface
	}
	const fondCase = (fond, c, diff) => {
		if (diff) return FOND_ECART
		if (fond === FOND_SURVOL) return FOND_SURVOL
		if (c.moment !== "NUIT") return fond
		return fond === FOND_ZEBRE ? FOND_N_ZEBRE : FOND_N
	}
	const sectionLigne = (titre) => {
		rang += 1
		return <Section titre={titre} />
	}
	const ligne = (row, fond, contenu) => (
		<tr
			key={row.code}
			className="no-hover"
			style={styles.tr(fond)}
			onMouseEnter={() => setSurvolLigne(row.code)}
			onMouseLeave={() => setSurvolLigne(null)}
		>
			<Presta row={row} fond={fond} />
			{contenu}
		</tr>
	)

	return (
		<>
			<div ref={carte} style={styles.card}>
				{QuitGuard}
				{confirm && (
					<CustomModal
						title={confirmTitles[confirm]}
						onClose={fermer}
						buttons={[
							{
								title: "Annuler",
								onClick: fermer,
								disabled: etat.loading,
							},
							{
								title:
									confirm === "supprimer"
										? "Supprimer"
										: "Confirmer",
								styleName:
									confirm === "supprimer"
										? "danger"
										: "primary",
								onClick: doAction,
								loading: etat.loading,
							},
						]}
					>
						{confirm === "sauver" && (
							<p>
								{nbEcarts} diffère(nt) de la version en vigueur.
								Le brouillon reste modifiable.
							</p>
						)}
						{confirm === "vigueur" && (
							<>
								<p>
									{nbEcarts} diffère(nt) de la version en
									vigueur.
								</p>
								<p>
									La grille s'appliquera aux calculs des
									missions privées dès le{" "}
									<b>{dateFr(draftDate)}</b> ; la version
									actuelle sera archivée. Les missions déjà
									calculées gardent leurs lignes.
								</p>
							</>
						)}
						{confirm === "supprimer" && (
							<p>
								Le brouillon et tous ses prix seront supprimés.
								Les versions en vigueur et archivées ne bougent
								pas.
							</p>
						)}
						{etat.erreur && (
							<p style={{ color: COLORS.danger }}>
								{etat.erreur}
							</p>
						)}
					</CustomModal>
				)}

				<div style={styles.head}>
					<div
						style={{
							display: "flex",
							alignItems: "flex-start",
							gap: 16,
						}}
					>
						<div>
							<div style={styles.title}>
								Tarifs des dépannages privés
							</div>
							<div style={styles.sub}>
								Grille appliquée par le serveur au calcul du
								prix des missions <b>privées</b> (bloc Tarif du
								dépanneur, et texte des deux prix à la
								création). Cellule vide = non disponible. Prix
								TTC en CHF.
							</div>
						</div>
						{isBoss && !brouillon && !erreurChargement && (
							<button
								style={{
									...gs.btnSecondary,
									marginLeft: "auto",
									whiteSpace: "nowrap",
								}}
								onClick={creerBrouillon}
							>
								+ Nouvelle version
							</button>
						)}
					</div>
				</div>

				{erreurChargement ? (
					<div style={styles.erreurChargement}>
						Grille indisponible : {erreurChargement}
					</div>
				) : (
					<>
						<div style={styles.rail}>
							<span style={styles.railLabel}>Versions</span>
							{liste.map((g) => {
								const isSelected = g.id === selectedId
								const isBrouillon = g.etat === ETAT_BROUILLON
								const dotColor = isBrouillon
									? COLORS.warning
									: isSelected
										? COLORS.accent
										: g.etat === ETAT_EN_VIGUEUR
											? COLORS.success
											: COLORS.textLight
								return (
									<button
										key={g.id}
										style={styles.pill(
											isSelected,
											isBrouillon,
										)}
										onClick={() => choisir(g)}
									>
										<span style={styles.dot(dotColor)} />
										{g.etat === ETAT_EN_VIGUEUR
											? `En vigueur depuis le ${dateFr(g.valable_des)}`
											: isBrouillon
												? "Brouillon · valable dès le"
												: g.etat === ETAT_ARCHIVEE &&
													`${dateFr(g.valable_des)} · archivée`}
										{isBrouillon && (
											<input
												type="date"
												aria-label="Date d'entrée en vigueur du brouillon"
												style={styles.dateInput}
												value={draftDate}
												min={enVigueur?.valable_des}
												disabled={!isBoss}
												onClick={(e) =>
													e.stopPropagation()
												}
												onChange={(e) =>
													setDraftDate(e.target.value)
												}
											/>
										)}
									</button>
								)
							})}
						</div>

						<div style={styles.toolbar}>
							<span style={styles.legende}>
								<span style={styles.segment}>
									<b>J</b> = 07h00 – 18h30
								</span>
								<span style={styles.segment}>
									<b>N</b> = 18h30 – 07h00, week-end (ven.
									18h30 → lun. 07h00) & fériés
								</span>
								<span style={styles.segment}>
									cellule vide = <b>non disponible</b>
								</span>
							</span>
							{editable ? (
								<label style={styles.aideSaisie}>
									<input
										type="checkbox"
										checked={autofillSous}
										onChange={(e) =>
											setAutofillSous(e.target.checked)
										}
									/>
									Sous-sol = Route + ½ h (suit la route tant
									qu'on ne le modifie pas)
								</label>
							) : (
								<span style={styles.lectureSeule}>
									<span role="img" aria-label="Verrou">
										🔒
									</span>{" "}
									{isBoss
										? "Version figée — lecture seule"
										: "Lecture seule (réservé au boss)"}
								</span>
							)}
						</div>

						<div style={styles.scroll}>
							<table style={styles.table}>
								<thead>
									<tr
										className="no-hover"
										style={styles.tr(FOND_TETE)}
									>
										<th
											style={{
												...styles.stickyCol(FOND_TETE),
												zIndex: 3,
												verticalAlign: "bottom",
												fontSize: 11.5,
												fontWeight: 600,
												color: COLORS.textMuted,
											}}
											rowSpan={3}
										>
											Prestation
										</th>
										{LIEUX.map((l, i) => (
											<th
												key={l.code}
												style={styles.zoneHead(i > 0)}
												colSpan={
													POIDS.length *
													MOMENTS.length
												}
											>
												{l.label}
											</th>
										))}
									</tr>
									<tr
										className="no-hover"
										style={styles.tr(FOND_TETE)}
									>
										{LIEUX.map((l, li) =>
											POIDS.map((p, pi) => (
												<th
													key={`${l.code}_${p.code}`}
													style={styles.catHead(
														li > 0 && pi === 0,
														pi > 0,
													)}
													colSpan={MOMENTS.length}
												>
													{p.label}
												</th>
											)),
										)}
									</tr>
									<tr
										className="no-hover"
										style={styles.tr(FOND_TETE)}
									>
										{COLS.map((c) => (
											<th
												key={`${c.lieu}_${c.poids}_${c.moment}`}
												style={styles.momentHead(c)}
											>
												{c.moment === "JOUR"
													? "J"
													: "N"}
											</th>
										))}
									</tr>
								</thead>
								<tbody style={styles.tbody}>
									{BASE.map((section) => (
										<React.Fragment key={section.titre}>
											{sectionLigne(section.titre)}
											{section.rows.map((row) => {
												const fond = fondLigne(row.code)
												return ligne(
													row,
													fond,
													COLS.map((c) => {
														const k = cleCellule(
															c.lieu,
															row.situation,
															c.moment,
															c.poids,
															row.issue,
														)
														const diff =
															editable &&
															diffKeys.includes(k)
														const v = shown[k]
														return (
															<td
																key={k}
																style={styles.cell(
																	c,
																	fondCase(
																		fond,
																		c,
																		diff,
																	),
																)}
																title={
																	diff
																		? titreEcart(
																				k,
																			)
																		: undefined
																}
															>
																{!dispo(
																	row,
																	c.lieu,
																) ? (
																	<span
																		style={
																			styles.na
																		}
																	>
																		—
																	</span>
																) : editable ? (
																	saisie(
																		k,
																		diff,
																		`${row.code} ${row.label}, ${c.lieu}, ${c.poids}, ${c.moment}`,
																	)
																) : v !=
																  null ? (
																	<span
																		style={
																			styles.cellValue
																		}
																	>
																		{v}
																	</span>
																) : (
																	<span
																		style={
																			styles.na
																		}
																	>
																		—
																	</span>
																)}
															</td>
														)
													}),
												)
											})}
										</React.Fragment>
									))}
									{SUPP.map((section) => (
										<React.Fragment key={section.titre}>
											{sectionLigne(section.titre)}
											{section.rows.map((row) => {
												const fond = fondLigne(row.code)
												return ligne(
													row,
													fond,
													<td
														style={styles.suppCell(
															fond,
														)}
														colSpan={COLS.length}
													>
														<div
															style={
																styles.suppVals
															}
														>
															{row.texte && (
																<span
																	style={
																		styles.suppTexte
																	}
																>
																	{row.texte}
																</span>
															)}
															{row.fixe && (
																<span
																	style={
																		styles.cellValue
																	}
																>
																	{row.fixe}
																	<span
																		style={
																			styles.unite
																		}
																	>
																		{
																			row.unite
																		}
																	</span>
																</span>
															)}
															{(row.u
																? [
																		[
																			null,
																			row.u,
																		],
																	]
																: (
																		row.jn ||
																		[]
																	).map(
																		(
																			code,
																			i,
																		) => [
																			i
																				? "N"
																				: "J",
																			code,
																		],
																	)
															).map(
																([m, code]) => {
																	const k =
																		cleSupp(
																			code,
																		)
																	const diff =
																		editable &&
																		diffKeys.includes(
																			k,
																		)
																	const v =
																		shown[k]
																	return (
																		<span
																			key={
																				code
																			}
																			style={
																				styles.suppMoment
																			}
																		>
																			{m}
																			<span
																				style={styles.suppBox(
																					diff,
																				)}
																				title={
																					diff
																						? titreEcart(
																								k,
																							)
																						: undefined
																				}
																			>
																				{editable ? (
																					saisie(
																						k,
																						diff,
																						`${row.code} ${row.label}${m ? `, ${m}` : ""}`,
																					)
																				) : (
																					<span
																						style={
																							styles.cellValue
																						}
																					>
																						{v ?? (
																							<span
																								style={
																									styles.na
																								}
																							>
																								—
																							</span>
																						)}
																					</span>
																				)}
																			</span>
																		</span>
																	)
																},
															)}
															<span
																style={
																	styles.tous
																}
															>
																tous lieux et
																poids
															</span>
														</div>
													</td>,
												)
											})}
										</React.Fragment>
									))}
								</tbody>
							</table>
						</div>

						<div style={styles.footnotes}>
							<span>
								* Pas de prix « sur place » sur autoroute ni
								pour un accident : transport d'office.
							</span>
							<span>
								** Le prix « transporté » comprend le
								chargement, le déchargement et 20 km en charge.
							</span>
							<span>
								*** Sous-sol : prix de la route + une demi-heure
								(5.1), sur place comme transporté.
							</span>
							<span>
								Transport de nuit : toujours vers le dépôt.
								Accident de nuit : pas de livraison directe au
								garage, livraison le lendemain depuis le dépôt
								(6.x).
							</span>
							<span>
								Client coché « sous contrat dépannage » : même
								calcul, rien à encaisser sur place (le bureau
								ajuste au besoin).
							</span>
						</div>
					</>
				)}

				{editable && (
					<>
						<div style={styles.savebar}>
							<div style={styles.savebarInner(cadre)}>
								<span
									style={{
										flex: "1 1 auto",
										minWidth: 0,
										fontSize: 14,
										fontWeight: 600,
										color: COLORS.text,
									}}
								>
									<b style={{ color: COLORS.warning }}>
										{diffKeys.length}
									</b>{" "}
									cellule(s) modifiée(s) par rapport à la
									version en vigueur
									{invalides.length > 0 && (
										<span style={{ color: COLORS.danger }}>
											{" "}
											·{" "}
											{pluriel(
												invalides.length,
												"case",
											)}{" "}
											à corriger
										</span>
									)}
								</span>
								<button
									style={{
										...styles.btnDiscret,
										...styles.nowrap,
									}}
									onClick={() => ouvrir("supprimer")}
								>
									Supprimer le brouillon
								</button>
								<button
									style={{
										...gs.btnSecondary,
										...styles.nowrap,
									}}
									onClick={() => ouvrir("sauver")}
								>
									Enregistrer le brouillon
								</button>
								<button
									style={{
										...gs.btnPrimary,
										...styles.nowrap,
									}}
									onClick={() => ouvrir("vigueur")}
								>
									Mettre en vigueur…
								</button>
							</div>
						</div>
					</>
				)}
			</div>
			{/* Cale HORS de la carte : réserve la hauteur de la barre fixe. */}
			{editable && <div style={styles.cale} />}
		</>
	)
}

export default TarifsPrivesV2
